import type { PaymentIntentResult, Stripe } from '@stripe/stripe-js';

import { toast } from 'react-toastify';

export const CARD_PAYMENT_SUCCEEDED_STATUS = 'succeeded';

const CARD_PAYMENT_SUCCESS_STATUSES = ['succeeded', 'processing'] as const;

const CARD_PAYMENT_FAILURE_STATUSES = [
  'requires_payment_method',
  'canceled',
  'requires_action',
] as const;

type CardPaymentStatus = (typeof CARD_PAYMENT_FAILURE_STATUSES)[number] | string;

const isFailurePaymentStatus = (status: CardPaymentStatus): boolean =>
  (CARD_PAYMENT_FAILURE_STATUSES as readonly string[]).includes(status);

export const getCardPaymentFailureMessage = (payload?: PaymentIntentResult): string => {
  if (payload?.error?.message) {
    return payload.error.message;
  }

  const lastPaymentErrorMessage = payload?.paymentIntent?.last_payment_error?.message;
  if (lastPaymentErrorMessage) {
    return lastPaymentErrorMessage;
  }

  const status = payload?.paymentIntent?.status;
  if (status) {
    return `Payment was not completed (${status}). Please try again or use a different card.`;
  }

  return 'Payment was not completed. Please try again or use a different card.';
};

export const isCardPaymentSucceeded = (payload?: PaymentIntentResult): boolean => {
  if (payload?.error) {
    return false;
  }

  const status = payload?.paymentIntent?.status;
  if (!status) {
    return false;
  }

  if (isFailurePaymentStatus(status)) {
    return false;
  }

  return (CARD_PAYMENT_SUCCESS_STATUSES as readonly string[]).includes(status);
};

export const handleCardPaymentResult = ({
  payload,
  endPaymentFlow,
  onSuccess,
}: {
  endPaymentFlow: () => void;
  onSuccess?: () => void;
  payload?: PaymentIntentResult;
}): boolean => {
  if (isCardPaymentSucceeded(payload)) {
    onSuccess?.();
    return true;
  }

  endPaymentFlow();
  toast(getCardPaymentFailureMessage(payload), {
    type: 'error',
    position: 'bottom-center',
  });
  return false;
};

export const handleCardPaymentResultAsync = async ({
  payload,
  stripe,
  clientSecret,
  endPaymentFlow,
  onSuccess,
}: {
  clientSecret: string;
  endPaymentFlow: () => void;
  onSuccess?: () => void;
  payload?: PaymentIntentResult;
  stripe: null | Stripe;
}): Promise<boolean> => {
  if (isCardPaymentSucceeded(payload)) {
    onSuccess?.();
    return true;
  }

  if (!payload?.error && stripe && clientSecret) {
    try {
      const retrieved = await stripe.retrievePaymentIntent(clientSecret);
      if (isCardPaymentSucceeded(retrieved)) {
        onSuccess?.();
        return true;
      }

      return handleCardPaymentResult({
        payload: retrieved,
        endPaymentFlow,
        onSuccess,
      });
    } catch {
      // Fall through to the original payload error handling.
    }
  }

  return handleCardPaymentResult({
    payload,
    endPaymentFlow,
    onSuccess,
  });
};
